import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Typography, FormControl, InputLabel, Select, Radio,
    MenuItem, RadioGroup, FormControlLabel, SelectChangeEvent,
} from '@mui/material';
import UserContext from '../../User/UserSettings/UserContext';

const MOCK_POTATO_VARIETIES = [
    { id: 'potato1', name: 'Test Variety' },
];

interface BenchmarkSettings {
    comparisonTargetType: string;
    comparisonTargetValue: string | null;
    averagingPeriodDays: number;
}

interface BenchmarkProps {
    selectedStorageId?: string | number; // ID of the current storage being viewed, to exclude from "ownStorage" list
    onSettingsChange: (settings: BenchmarkSettings) => void;
    initialSettings?: BenchmarkSettings;
}

function Benchmark({ onSettingsChange, initialSettings, selectedStorageId }: BenchmarkProps) {
    const [comparisonTargetType, setComparisonTargetType] = useState<string>(initialSettings?.comparisonTargetType || 'none');
    const [selectedOwnStorage, setSelectedOwnStorage] = useState<string>(initialSettings?.comparisonTargetType === 'ownStorage' ? initialSettings.comparisonTargetValue || '' : '');
    const [selectedGeoArea, setSelectedGeoArea] = useState<string>(initialSettings?.comparisonTargetType === 'geoArea' ? initialSettings.comparisonTargetValue || '' : '');
    const [selectedPotatoVariety, setSelectedPotatoVariety] = useState<string>(initialSettings?.comparisonTargetType === 'potatoVariety' ? initialSettings.comparisonTargetValue || '' : '');
    const { user } = useContext(UserContext) || { user: { locations: [] } };

    // Check if "Another of My Storages" option should be disabled
    const availableStoragesCount = user.locations.filter((storage: any) =>
        !(selectedStorageId && storage.id.toString() === selectedStorageId.toString())
    ).length;
    const isOwnStorageDisabled = user.locations.length <= 1 || availableStoragesCount === 0;

    // Reset selectedOwnStorage if it matches the currently selected storage
    useEffect(() => {
        if (selectedStorageId && selectedOwnStorage && selectedOwnStorage.toString() === selectedStorageId.toString()) {
            setSelectedOwnStorage('');
            console.log('Benchmark storage selection reset due to conflict with main storage selection');
        }
    }, [selectedStorageId, selectedOwnStorage]);

    // Reset comparison type to 'none' if 'ownStorage' becomes unavailable
    useEffect(() => {
        if (comparisonTargetType === 'ownStorage' && isOwnStorageDisabled) {
            setComparisonTargetType('none');
            setSelectedOwnStorage('');
            console.log('Benchmark comparison type reset to "none" due to no available storages');
        }
    }, [comparisonTargetType, isOwnStorageDisabled]);

    useEffect(() => {
        let targetValue: string | null = null;
        if (comparisonTargetType === 'ownStorage') targetValue = selectedOwnStorage;
        else if (comparisonTargetType === 'geoArea') targetValue = selectedGeoArea;
        else if (comparisonTargetType === 'potatoVariety') targetValue = selectedPotatoVariety;

        onSettingsChange({
            comparisonTargetType,
            comparisonTargetValue: targetValue,
            averagingPeriodDays: 0, // Default to 0 for now, can be enhanced later
        });
    }, [comparisonTargetType, selectedOwnStorage, selectedGeoArea, selectedPotatoVariety]);

    const handleComparisonTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newType = event.target.value as 'none' | 'ownStorage' | 'geoArea' | 'potatoVariety';
        setComparisonTargetType(newType);
        // Reset selections when type changes
        setSelectedOwnStorage('');
        setSelectedGeoArea('');
        setSelectedPotatoVariety('');
    };

    return (
        <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                Benchmark Configuration
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Compare your data to:</Typography>
                <RadioGroup
                    row
                    aria-label="comparison-target-type"
                    name="comparison-target-type-group"
                    value={comparisonTargetType}
                    onChange={handleComparisonTypeChange}
                >
                    <FormControlLabel value="none" control={<Radio />} label="None" />
                    <FormControlLabel
                        value="ownStorage"
                        control={<Radio />}
                        label="Another of My Storages"
                        disabled={isOwnStorageDisabled}
                    />
                    <FormControlLabel value="geoArea" control={<Radio />} label="Geographical Area" />
                    <FormControlLabel value="potatoVariety" control={<Radio />} label="Potato Variety (coming soon)" />
                </RadioGroup>
            </FormControl>

            {comparisonTargetType === 'ownStorage' && (
                <Box>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="select-own-storage-label">Select Your Storage</InputLabel>
                        <Select
                            labelId="select-own-storage-label"
                            value={selectedOwnStorage}
                            label="Select Your Storage"
                            onChange={(e: SelectChangeEvent<string>) => setSelectedOwnStorage(e.target.value)}
                        >
                            {user.locations.map((storage: { id: string; locationName: string }) => {
                                const isCurrentStorage = Boolean(selectedStorageId && storage.id.toString() === selectedStorageId.toString());
                                return (
                                    <MenuItem
                                        key={storage.id}
                                        value={storage.id}
                                        disabled={isCurrentStorage}
                                        sx={{
                                            opacity: isCurrentStorage ? 0.5 : 1,
                                            fontStyle: isCurrentStorage ? 'italic' : 'normal'
                                        }}
                                    >
                                        {storage.locationName}
                                        {isCurrentStorage && ' (Currently Selected)'}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>

                    {/* Help text */}
                    {user.locations.length > 1 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {user.locations.filter((storage: any) =>
                                !(selectedStorageId && storage.id.toString() === selectedStorageId.toString())
                            ).length === 0
                                ? "No other storages available for comparison. Select a different storage in the Data & Storage tab to enable this feature."
                                : "Select another storage to compare your current data against."
                            }
                        </Typography>
                    )}

                    {user.locations.length === 1 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            You only have one storage location. This feature requires multiple storage locations.
                        </Typography>
                    )}
                </Box>
            )}
            {comparisonTargetType === 'potatoVariety' && (
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="select-potato-variety-label">Select Potato Variety</InputLabel>
                    <Select
                        labelId="select-potato-variety-label"
                        value={selectedPotatoVariety}
                        label="Select Potato Variety"
                        onChange={(e: SelectChangeEvent<string>) => setSelectedPotatoVariety(e.target.value)}
                    >
                        {MOCK_POTATO_VARIETIES.map(variety => (
                            <MenuItem key={variety.id} value={variety.id}>{variety.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );
}

export default Benchmark;